import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { assignmentsAPI, sedesAPI } from '../../services/api';
import { Calendar, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { Toaster, toast } from 'sonner';
import { format, startOfWeek, addDays } from 'date-fns';

const AssignmentsPage = () => {
  const [sedes, setSedes] = useState([]);
  const [selectedSede, setSelectedSede] = useState('');
  const [weekStartDate, setWeekStartDate] = useState('');
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSedes();
    const today = new Date();
    const thisMonday = startOfWeek(today, { weekStartsOn: 1 });
    setWeekStartDate(format(thisMonday, 'yyyy-MM-dd'));
  }, []);

  useEffect(() => {
    if (selectedSede && weekStartDate) {
      loadAssignments();
    }
  }, [selectedSede, weekStartDate]);

  const loadSedes = async () => {
    try {
      const response = await sedesAPI.getAll();
      setSedes(response.data);
      if (response.data.length > 0) {
        setSelectedSede(response.data[0].id);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAssignments = async () => {
    setLoading(true);
    try {
      const response = await assignmentsAPI.getAll({ sede_id: selectedSede, week_start_date: weekStartDate });
      setAssignments(response.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async (id) => {
    if (!window.confirm('¿Cancelar esta asignación?')) return;
    try {
      await assignmentsAPI.cancel(id);
      toast.success('Asignación cancelada');
      loadAssignments();
    } catch (error) {
      toast.error('Error al cancelar');
    }
  };

  const getDayName = (dayOfWeek) => {
    const days = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
    return days[dayOfWeek];
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'confirmed': return 'badge-success';
      case 'cancelled': return 'badge-error';
      case 'no_show': return 'badge-warning';
      default: return 'badge-info';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'assigned': return 'Asignado';
      case 'confirmed': return 'Confirmado';
      case 'cancelled': return 'Cancelado';
      case 'no_show': return 'No asistió';
      default: return status;
    }
  };

  // Group by day
  const byDay = assignments.reduce((acc, a) => {
    const day = a.day_of_week;
    if (!acc[day]) acc[day] = [];
    acc[day].push(a);
    return acc;
  }, {});

  return (
    <AdminLayout>
      <Toaster position="top-right" />
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight" style={{ fontFamily: 'Cabinet Grotesk' }}>
              Asignaciones Semanales
            </h1>
            <p className="text-slate-500 text-sm mt-1">
              Vista de asignaciones por semana
            </p>
          </div>
          <div className="flex gap-2">
            <Select value={selectedSede} onValueChange={setSelectedSede}>
              <SelectTrigger className="w-[160px] rounded-sm">
                <SelectValue placeholder="Sede" />
              </SelectTrigger>
              <SelectContent>
                {sedes.map((s) => (
                  <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <input
              type="date"
              value={weekStartDate}
              onChange={(e) => setWeekStartDate(e.target.value)}
              className="form-input w-[160px]"
            />
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-[#002FA7]" />
          </div>
        ) : assignments.length === 0 ? (
          <Card className="border border-slate-200">
            <CardContent className="flex flex-col items-center justify-center h-64 text-center">
              <Calendar className="w-12 h-12 text-slate-300 mb-4" />
              <p className="text-slate-500">No hay asignaciones para esta semana</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
            {[0, 1, 2, 3, 4].map((day) => {
              const dayAssignments = byDay[day] || [];
              const dayDate = addDays(new Date(weekStartDate), day);
              return (
                <Card key={day} className="border border-slate-200">
                  <CardHeader className="pb-2 bg-slate-50 border-b border-slate-200">
                    <CardTitle className="text-base flex items-center justify-between">
                      <span>{getDayName(day)}</span>
                      <span className="text-xs font-normal text-slate-500">
                        {format(dayDate, 'dd/MM')}
                      </span>
                    </CardTitle>
                    <p className="text-xs text-slate-500">{dayAssignments.length} asignaciones</p>
                  </CardHeader>
                  <CardContent className="p-0 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 280px)' }}>
                    {dayAssignments.length === 0 ? (
                      <p className="text-sm text-slate-400 p-4 text-center">Sin asignaciones</p>
                    ) : (
                      dayAssignments.map((a) => (
                        <div key={a.id} className="p-3 border-b border-slate-100 last:border-0 hover:bg-slate-50">
                          <div className="flex items-start justify-between">
                            <div>
                              <p className="font-medium text-sm">{a.user_name}</p>
                              <span className={`${getStatusBadge(a.status)} mt-1`}>
                                {getStatusLabel(a.status)}
                              </span>
                            </div>
                            {a.status === 'assigned' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleCancel(a.id)}
                                className="text-slate-400 hover:text-red-500 h-7 w-7 p-0"
                              >
                                <XCircle className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                          {a.assignment_reason && (
                            <p className="text-xs text-slate-500 mt-2">{a.assignment_reason}</p>
                          )}
                          {a.equity_score && (
                            <p className="text-xs text-slate-400 mt-1">Equidad: {a.equity_score}%</p>
                          )}
                        </div>
                      ))
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AssignmentsPage;
